if (!customElements.get('subscription-freebie-cart')) {
  const ATTR_ENABLED = '_sf_enabled';
  const ATTR_ELIGIBLE = '_sf_eligible_variant_ids';
  const ATTR_FREEBIE = '_sf_freebie_variant_id';
  const ATTR_EXCLUDE_BUNDLE = '_sf_exclude_bundle';
  const PROP_FREEBIE = '_sub_freebie';
  const PROP_BUNDLE = '_is_it_bundle';
  const INITIATOR = 'subscription-freebie';

  let isReconciling = false;
  let hasAttemptedAdd = false;

  /**
   * Subscription freebie: auto-add / auto-remove via Liquid Ajax Cart.
   * Progress bar owns liquidAjaxCart.conf('mutations'), so this reconciles on
   * queue-end. Cart attributes for the Discount
   * Function are synced once on load, not on every cart change.
   * @see https://liquid-ajax-cart.js.org/v2/events/
   */
  customElements.define(
    'subscription-freebie-cart',
    class SubscriptionFreebieCart extends HTMLElement {
      constructor() {
        super();
        this.onCartReady = this.onCartReady.bind(this);
        this.cleanup = this.cleanup.bind(this);
        this.reconcile = this.reconcile.bind(this);
      }

      /**
       * @param {string|undefined} value - Comma or whitespace separated variant IDs
       * @returns {Set<number>}
       */
      static parseIds(value) {
        const ids = new Set();

        if (!value) {
          return ids;
        }

        for (const part of value.split(/[\s,]+/)) {
          const id = SubscriptionFreebieCart.parseId(part);

          if (id !== null) {
            ids.add(id);
          }
        }

        return ids;
      }

      /**
       * @param {string|undefined} value - A single variant ID
       * @returns {number|null} The ID, or null unless it is a whole, positive, safe integer
       */
      static parseId(value) {
        const part = value?.trim() ?? '';

        if (!/^\d+$/.test(part)) {
          return null;
        }

        const id = Number(part);

        return Number.isSafeInteger(id) && id > 0 ? id : null;
      }

      /**
       * @param {Object} item - Liquid Ajax Cart line item
       * @returns {boolean}
       */
      static isFreebieLine(item) {
        return String(item?.properties?.[PROP_FREEBIE]) === 'true';
      }

      connectedCallback() {
        this.eligibleVariantIds = SubscriptionFreebieCart.parseIds(this.dataset.eligibleVariants);
        this.freebieVariantId = SubscriptionFreebieCart.parseId(this.dataset.freebieVariant);
        this.excludeBundle = this.dataset.excludeBundle === 'true';

        const isActive = this.dataset.enabled === 'true' && this.eligibleVariantIds.size > 0 && this.freebieVariantId !== null;

        this.readyHandler = isActive ? this.onCartReady : this.cleanup;

        if (isActive) {
          document.addEventListener('liquid-ajax-cart:queue-end', this.reconcile);
        }

        if (window.liquidAjaxCart?.cart) {
          this.readyHandler();
        } else {
          document.addEventListener('liquid-ajax-cart:init', this.readyHandler, { once: true });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('liquid-ajax-cart:queue-end', this.reconcile);
        document.removeEventListener('liquid-ajax-cart:init', this.readyHandler);
      }

      /**
       * Cart attributes the Discount Function reads.
       * @returns {Record<string, string>}
       */
      desiredAttributes() {
        return {
          [ATTR_ENABLED]: 'true',
          [ATTR_ELIGIBLE]: Array.from(this.eligibleVariantIds).join(','),
          [ATTR_FREEBIE]: String(this.freebieVariantId),
          [ATTR_EXCLUDE_BUNDLE]: String(this.excludeBundle),
        };
      }

      /**
       * Tags requests so the page loader and GTM skip them, and releases the lock.
       * @param {Function} [afterSettle] - Runs after the request completes
       * @returns {Object} Liquid Ajax Cart request options
       */
      requestOptions(afterSettle) {
        return {
          info: { initiator: INITIATOR },
          lastCallback: () => {
            isReconciling = false;
            afterSettle?.();
          },
        };
      }

      /**
       * Promo active: sync cart attributes once on load if they differ, then reconcile.
       * @returns {void}
       */
      onCartReady() {
        const liquidAjaxCart = window.liquidAjaxCart;

        if (!liquidAjaxCart?.cart || !liquidAjaxCart.update) {
          return;
        }

        const current = liquidAjaxCart.cart.attributes || {};
        const desired = this.desiredAttributes();
        const needsUpdate = Object.entries(desired).some(([key, value]) => (current[key] ?? '') !== value);

        if (!needsUpdate || isReconciling) {
          this.reconcile();
          return;
        }

        isReconciling = true;
        liquidAjaxCart.update({ attributes: desired }, this.requestOptions(this.reconcile));
      }

      /**
       * Promo off or misconfigured: clear leftover _sf_* attributes and remove
       * freebie lines in one request. Sends nothing once the cart is clean.
       * @returns {void}
       */
      cleanup() {
        const liquidAjaxCart = window.liquidAjaxCart;

        if (!liquidAjaxCart?.cart?.items || !liquidAjaxCart.update || isReconciling) {
          return;
        }

        const current = liquidAjaxCart.cart.attributes || {};
        const attributes = {};
        const updates = {};

        for (const key of [ATTR_ENABLED, ATTR_ELIGIBLE, ATTR_FREEBIE, ATTR_EXCLUDE_BUNDLE]) {
          if (current[key] != null && current[key] !== '') {
            attributes[key] = '';
          }
        }

        for (const item of liquidAjaxCart.cart.items) {
          if (item?.key && SubscriptionFreebieCart.isFreebieLine(item)) {
            updates[item.key] = 0;
          }
        }

        if (!Object.keys(attributes).length && !Object.keys(updates).length) {
          return;
        }

        isReconciling = true;
        liquidAjaxCart.update({ attributes, updates }, this.requestOptions());
      }

      /**
       * A subscription line for an eligible variant, not the freebie itself and,
       * with Exclude Bundles on, not a bundle line (_is_it_bundle=true).
       * @param {Object} item - Liquid Ajax Cart line item
       * @returns {boolean}
       */
      isQualifyingLine(item) {
        if (!item?.selling_plan_allocation || SubscriptionFreebieCart.isFreebieLine(item)) {
          return false;
        }

        if (!this.eligibleVariantIds.has(Number(item.variant_id || item.id))) {
          return false;
        }

        return !(this.excludeBundle && String(item.properties?.[PROP_BUNDLE]) === 'true');
      }

      /**
       * Reconcile the freebie against the settled cart.
       * @returns {void}
       */
      reconcile() {
        if (isReconciling) {
          return;
        }

        const liquidAjaxCart = window.liquidAjaxCart;

        if (!liquidAjaxCart?.cart?.items || !liquidAjaxCart.add || !liquidAjaxCart.update) {
          return;
        }

        const items = liquidAjaxCart.cart.items;
        const freebieItems = items.filter((item) => SubscriptionFreebieCart.isFreebieLine(item));
        const shouldHaveFreebie = items.some((item) => this.isQualifyingLine(item));
        if (!shouldHaveFreebie) {
          hasAttemptedAdd = false;
          this.removeLines(freebieItems);
          return;
        }

        if (!freebieItems.length) {
          if (hasAttemptedAdd) {
            return;
          }

          hasAttemptedAdd = true;
          isReconciling = true;
          liquidAjaxCart.add(
            {
              items: [
                {
                  id: this.freebieVariantId,
                  quantity: 1,
                  properties: { [PROP_FREEBIE]: 'true' },
                },
              ],
            },
            this.requestOptions(),
          );
          return;
        }

        // Only normalise free lines; a priced freebie shows its controls and is left as the customer set it.
        const [keep, ...extras] = freebieItems.filter((item) => item.final_line_price === 0);
        const updates = {};

        for (const item of extras) {
          if (item?.key) {
            updates[item.key] = 0;
          }
        }

        if (keep?.key && keep.quantity !== 1) {
          updates[keep.key] = 1;
        }

        this.sendUpdates(updates);
      }

      /**
       * @param {Array<Object>} items - Line items to set to quantity 0
       * @returns {void}
       */
      removeLines(items) {
        const updates = {};

        for (const item of items) {
          if (item?.key) {
            updates[item.key] = 0;
          }
        }

        this.sendUpdates(updates);
      }

      /**
       * @param {Record<string, number>} updates - Line key to quantity
       * @returns {void}
       */
      sendUpdates(updates) {
        if (!Object.keys(updates).length) {
          return;
        }

        isReconciling = true;
        window.liquidAjaxCart.update({ updates }, this.requestOptions());
      }
    },
  );
}
